/* eslint-disable react-hooks/exhaustive-deps */
import { Table, Button, Grid, Tooltip } from "antd";
import axios from "axios";
import { useEffect, useState } from "react";

import CreateEditConfigModal from "./CreateEditConfigModal";
import { showErrorNotification, showSuccessNotification } from "../../utils/notification";
import { clamp } from "../../utils/responsiveHelpers";

/**
 * Truncates a cell to a single line and exposes the untruncated content on hover.
 * Config values are raw JSON blobs, so an unconstrained cell stretches the table.
 *
 * @param {string} cellContent - Text to display.
 * @returns {React.ReactNode}
 */
const renderTruncatedCell = (cellContent) => (
  <Tooltip placement="topLeft" title={cellContent}>
    {cellContent}
  </Tooltip>
);

const AppConfigManagementDash = ({ userConfig, ROOT_URL }) => {
  const screens = Grid.useBreakpoint();
  const isMobileView = screens.md === false;

  const BASE_URL = "admin/app-config";

  const [supportedValueTypes, setSupportedValueTypes] = useState({});

  const [currentConfigId, setCurrentConfigId] = useState();
  const [initialValues, setInitialValues] = useState({});
  const [isConfigCreationModalOpen, setIsConfigCreationModalOpen] = useState(false);

  const [configsList, setConfigsList] = useState([]);

  const fetchConfigsList = () => {
    axios
      .get(`${ROOT_URL}/${BASE_URL}/list`, {})
      .then((res) => {
        const { configsList, valueTypes } = res.data;
        setConfigsList(configsList);
        setSupportedValueTypes(valueTypes);
        showSuccessNotification("Configs fetched successfully!");
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleCreateEditConfig = (formValues) => {
    axios
      .post(`${ROOT_URL}/${BASE_URL}/${currentConfigId === undefined ? "create" : "update"}`, {
        configParams: {
          ...formValues,
          configId: currentConfigId
        }
      })
      .then(() => {
        showSuccessNotification("Config created successfully!");
        setIsConfigCreationModalOpen(false);
        fetchConfigsList();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      key: "index",
      width: 70
    },
    {
      title: "Config Key",
      dataIndex: "key",
      key: "key",
      width: 240,
      ellipsis: { showTitle: false },
      render: renderTruncatedCell
    },
    {
      title: "Value",
      dataIndex: "displayValue",
      key: "displayValue",
      width: 320,
      ellipsis: { showTitle: false },
      render: renderTruncatedCell
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 250,
      ellipsis: { showTitle: false },
      render: renderTruncatedCell
    },
    {
      title: "Value Type",
      dataIndex: "valueType",
      key: "valueType",
      width: 120
    },
    {
      title: "Created by",
      dataIndex: "createdBy",
      key: "createdBy",
      width: 150
    },
    {
      title: "Actions",
      key: "action",
      width: 240,
      fixed: isMobileView ? undefined : "right",
      render: (row) => (
        <div
          id="admin_app_config_row_actions"
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-around",
            gap: "8px"
          }}
        >
          <Button
            type="primary"
            disabled={
              !(
                userConfig.appConfig.hasSudoEditAccess ||
                (userConfig.appConfig.hasLimitedEditAccess && row.isCreatedByCurrentUser)
              )
            }
            onClick={() => {
              setInitialValues(row);
              setIsConfigCreationModalOpen(true);
              setCurrentConfigId(row.id);
            }}
          >
            Modify Value/Description
          </Button>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchConfigsList();
  }, []);

  return (
    <div
      style={{
        marginTop: "20px",
        marginLeft: clamp(12, 30),
        marginRight: clamp(12, 30)
      }}
    >
      <div
        style={{
          marginBottom: "10px"
        }}
      >
        {userConfig.appConfig.hasCreationAccess && (
          <Button
            type="primary"
            onClick={() => {
              setCurrentConfigId(undefined);
              setInitialValues({});
              setIsConfigCreationModalOpen(true);
            }}
          >
            Create New Config
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={configsList}
        bordered
        pagination={false}
        // Fixed total width so a long JSON value cannot stretch the other columns
        scroll={{ x: 1370 }}
      />
      {isConfigCreationModalOpen && (
        <CreateEditConfigModal
          isModalOpen={isConfigCreationModalOpen}
          supportedValueTypes={supportedValueTypes}
          setIsModalOpen={setIsConfigCreationModalOpen}
          handleCreateEditConfig={handleCreateEditConfig}
          initialValues={initialValues}
        />
      )}
    </div>
  );
};

export default AppConfigManagementDash;
